import { z } from 'zod';

import { localeSchema } from './locales.js';
import { listQuerySchema } from './common.js';
import { demoRequestStatusSchema, type DemoRequestStatus } from './enums.js';
import { PlanTier, type PlanTier as PlanTierType } from './plans.js';

/**
 * SATIŞ OPERASYONU — demo talebi hunisi ve atanmış hesap yöneticisi.
 * Genişleme Adım 9.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `leads.ts` ile KARIŞTIRILMAMALI
 * ═══════════════════════════════════════════════════════════════════════════
 * `Lead` reklam platformlarından senkronlanan MÜŞTERİNİN lead'i — bizim
 * müşterimizin kendi formu. Buradaki `DemoRequest` BİZE gelen aday müşteri.
 * Aynı kelime, iki ayrı yön; tek tabloda toplamak kiracı yalıtımını
 * anlamsızlaştırırdı (birinin `workspaceId`si var, diğerinin yok).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Demo talebi — herkese açık form
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `POST /sales/demo-requests` gövdesi. Uç `@Public()`.
 *
 * 🔴 SPAM YÜZEYİ. Üç katman: dar hız sınırı (`@Throttle`), `website`
 * alanının YANINDA duran bal küpü (aşağıya bak) ve alan uzunluk sınırları.
 * Doğrulama kodu (captcha) YOK — üçüncü taraf servis bağlamak bu adımın
 * kapsamı değil ve kullanıcı kararı gerektirir; devir notunda yazılı.
 */
export const demoRequestInputSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: z.string().trim().email().max(160),
  phone: z.string().trim().max(32).optional(),
  companyName: z.string().trim().max(160).optional(),
  /// Aday müşterinin sitesi — GERÇEK alan, bal küpü değil.
  website: z.string().trim().max(200).optional(),
  monthlyAdSpend: z.string().trim().max(40).optional(),
  message: z.string().trim().max(2000).optional(),
  locale: localeSchema.default('tr'),
  /// Hangi sayfadan geldi. Sunucu bunu doğrulamıyor, olduğu gibi saklıyor:
  /// pazarlama ölçümü, yetki kararı değil.
  source: z.string().trim().max(200).optional(),

  /**
   * 🔴 BAL KÜPÜ — insan bunu görmez, bot doldurur.
   *
   * Dolu gelirse istek 202 dönüyor ama SATIR YAZILMIYOR. Neden 202: bota
   * "yakalandın" demek, bir sonraki denemesinde alanı boş bırakmasını
   * öğretmek olurdu. İnsan zaten hiç göremediği için yanlış pozitif yok.
   *
   * Adı `company_website` — bot için inandırıcı, bizim gerçek `website`
   * alanımızdan ayrı.
   */
  company_website: z.string().max(200).optional(),
});
export type DemoRequestInput = z.infer<typeof demoRequestInputSchema>;

/// Formun cevabı. Kayıt kimliği DÖNMÜYOR: herkese açık bir uçtan kimlik
/// döndürmek, sıralı olmasa bile numaralandırmaya davettir.
export interface DemoRequestAck {
  received: true;
  /// Ne zaman dönüleceği — arayüzde "1 iş günü içinde" yazacak metin
  /// buradan değil çeviriden geliyor; bu alan yalnız SLA saatini taşıyor.
  responseHours: number;
}

export const DEMO_RESPONSE_HOURS = 24;

// ═══════════════════════════════════════════════════════════════════════════
// /admin — huni yönetimi
// ═══════════════════════════════════════════════════════════════════════════

export interface DemoRequestRow {
  id: string;
  fullName: string;
  email: string;
  phone: string | null;
  companyName: string | null;
  website: string | null;
  monthlyAdSpend: string | null;
  message: string | null;
  locale: string;
  source: string | null;
  status: DemoRequestStatus;
  assignedTo: { userId: string; name: string | null; email: string } | null;
  meetingAt: string | null;
  meetingUrl: string | null;
  notes: string | null;
  grantedWorkspaceId: string | null;
  grantedPlan: PlanTierType | null;
  grantedUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export const demoRequestListQuerySchema = listQuerySchema.extend({
  status: demoRequestStatusSchema.optional(),
  assignedToUserId: z.string().uuid().optional(),
});
export type DemoRequestListQuery = z.infer<typeof demoRequestListQuerySchema>;

/**
 * `PATCH /admin/sales/demo-requests/:id`.
 *
 * Durum, atama ve toplantı TEK uçtan güncelleniyor. Üç ayrı uç yazmak,
 * "atadım ama durumu değiştirmeyi unuttum" hâlini normalleştirirdi.
 */
export const demoRequestUpdateSchema = z
  .object({
    status: demoRequestStatusSchema.optional(),
    assignedToUserId: z.string().uuid().nullable().optional(),
    meetingAt: z.coerce.date().nullable().optional(),
    meetingUrl: z.string().trim().url().max(500).nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Güncellenecek alan yok.' });
export type DemoRequestUpdateInput = z.infer<typeof demoRequestUpdateSchema>;

/**
 * `POST /admin/sales/demo-requests/:id/grant` — 14 gün tam erişim.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 SÜRE VE KADEME ZORUNLU, VARSAYILAN YOK — hayır, tam tersi
 * ═══════════════════════════════════════════════════════════════════════════
 * `days` varsayılanı 14 ve `plan` varsayılanı PRO. Varsayılanın olması
 * bilinçli: bu akışın TANIMI "14 gün tam erişim" ve her seferinde elle
 * yazdırmak, birinin 140 yazmasına açık kapı bırakırdı. Üst sınır 90 gün.
 *
 * `workspaceId` ZORUNLU: erişim bir çalışma alanına veriliyor, bir
 * e-postaya değil. Aday önce kayıt olur, sonra hibe verilir. Ters sıra
 * (önce hibe, sonra kayıt) "hangi hesaba" sorusunu cevapsız bırakırdı.
 */
export const demoGrantSchema = z.object({
  workspaceId: z.string().uuid(),
  plan: z.enum([PlanTier.STARTER, PlanTier.GROWTH, PlanTier.PRO, PlanTier.ENTERPRISE]).default(
    PlanTier.PRO,
  ),
  days: z.number().int().min(1).max(90).default(14),
});
export type DemoGrantInput = z.infer<typeof demoGrantSchema>;

export interface DemoGrantResponse {
  workspaceId: string;
  plan: PlanTierType;
  grantedUntil: string;
  /// Hibe bitince hangi kademeye düşecek. Bugün her zaman TRIAL —
  /// ödeme yapmış bir çalışma alanına hibe verilmiyor (uç reddediyor).
  revertsTo: PlanTierType;
}

// ═══════════════════════════════════════════════════════════════════════════
// Hesap yöneticisi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Panelde gösterilen kart. `GET /workspaces/account-manager`.
 *
 * 🔴 `bookingUrl` null ise arayüz "toplantı ayarla" düğmesini GÖSTERMEZ.
 * Çalışmayan bir düğme göstermek, kullanıcıya boş bir sayfa açtırmaktır.
 */
export interface AccountManagerCard {
  name: string;
  title: string | null;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  bookingUrl: string | null;
  timezone: string;
  languages: string[];
}

/// Kart yoksa `null` — "atanmış yöneticiniz yok" demek. Boş bir kart
/// döndürüp arayüzde iskelet göstermek, olmayan bir kişiyi var göstermek
/// olurdu.
export interface AccountManagerCardResponse {
  manager: AccountManagerCard | null;
}

export const accountManagerUpsertSchema = z.object({
  userId: z.string().uuid(),
  title: z.string().trim().max(120).nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  bookingUrl: z.string().trim().url().max(500).nullable().optional(),
  avatarUrl: z.string().trim().url().max(500).nullable().optional(),
  timezone: z.string().trim().max(64).default('Europe/Istanbul'),
  languages: z.array(localeSchema).max(13).default(['tr']),
  active: z.boolean().default(true),
});
export type AccountManagerUpsertInput = z.infer<typeof accountManagerUpsertSchema>;

export interface AccountManagerRow extends AccountManagerCard {
  id: string;
  userId: string;
  active: boolean;
  /// Kaç çalışma alanına atanmış — yeni atama yaparken yük dengesi.
  workspaceCount: number;
}

/// `PUT /admin/workspaces/:id/account-manager`. `managerId: null` atamayı
/// kaldırır.
export const accountManagerAssignSchema = z.object({
  managerId: z.string().uuid().nullable(),
});
export type AccountManagerAssignInput = z.infer<typeof accountManagerAssignSchema>;

export const SalesErrorCode = {
  DEMO_REQUEST_NOT_FOUND: 'DEMO_REQUEST_NOT_FOUND',
  /// Ödeme yapmış çalışma alanına hibe verilemez.
  GRANT_ON_PAID_WORKSPACE: 'GRANT_ON_PAID_WORKSPACE',
  ACCOUNT_MANAGER_NOT_FOUND: 'ACCOUNT_MANAGER_NOT_FOUND',
  /// Yönetici olarak atanacak kişi platform yöneticisi değil.
  NOT_A_PLATFORM_ADMIN: 'NOT_A_PLATFORM_ADMIN',
} as const;
export type SalesErrorCode = (typeof SalesErrorCode)[keyof typeof SalesErrorCode];
